/**
 * Panoul de tuning (Etapa 7): editorul de hărți ECU, piesele, standul de putere și diagnoza OBD.
 * Hărțile sunt pe turație, la sarcină plină; celulele se selectează cu degetul (una sau mai multe) și se modifică
 * cu pași sau procentual. Undo/redo, salvare în IndexedDB, export/import cu checksum, preseturi Stage 1/2/3.
 */
import type { PowertrainInfo } from '../tuning/info';
import { applyStage, type Stage } from '../tuning/presets';
import { exportCalibration, importCalibration, loadCalibration, saveCalibration, deleteCalibration } from '../tuning/store';
import { MAPS, STOCK_PARTS, dynoRows, type DynoRow, type Ecu, type MapKey, type Parts } from '../tuning/types';

export interface EcuPanelHost {
  carId: string;
  carName: string;
  info: PowertrainInfo;
  stock: Ecu;
  current: Ecu;
  parts: Parts;
  apply(ecu: Ecu, parts: Parts): void;
  dyno(ecu: Ecu, parts: Parts): Promise<Float32Array>;
  telemetry(): Float32Array | null;
  onClose(): void;
}

type Tab = 'maps' | 'parts' | 'dyno' | 'obd';
const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);
const clone = <T,>(x: T): T => JSON.parse(JSON.stringify(x));

const CODES: Array<[number, string, string]> = [
  [1, 'P0299', 'Presiune turbo prea mică (turbo avariat sau scăpări)'],
  [2, 'P0234', 'Suprapresiune: compresorul dus peste hartă'],
  [4, 'P0300', 'Rateuri: pistoane avariate'],
  [8, 'P0217', 'Motorul s-a supraîncălzit'],
  [16, 'P0325', 'Detonație severă'],
  [32, 'P0730', 'Raport de transmisie incorect: treaptă ruptă']
];

export function openEcuPanel(root: HTMLElement, h: EcuPanelHost): void {
  let tab: Tab = 'maps';
  let mapKey: MapKey = h.info.turbo ? 'boostBar' : h.info.fuel === 'diesel' ? 'fuel' : 'timingDeg';
  let ecu = clone(h.current), parts = clone(h.parts);
  const sel = new Set<number>();
  const undo: Array<{ ecu: Ecu; parts: Parts }> = [], redo: Array<{ ecu: Ecu; parts: Parts }> = [];
  let dyno: { stock: DynoRow[]; cur: DynoRow[] } | null = null;
  let status = '';
  let obdTimer = 0;

  const maps = MAPS.filter((m) => (!m.turbo || h.info.turbo) && (!m.petrol || h.info.fuel === 'petrol'));
  const noEcu = !h.info.ecu;

  function push(): void {
    undo.push({ ecu: clone(ecu), parts: clone(parts) });
    if (undo.length > 100) undo.shift();
    redo.length = 0;
  }

  function change(fn: () => void): void {
    push();
    fn();
    h.apply(ecu, parts);
    dyno = null;
    render();
  }

  function mapTab(): string {
    const m = MAPS.find((x) => x.key === mapKey)!;
    const cur = ecu[mapKey], st = h.stock[mapKey];
    const fmt = (v: number) => (m.step < 0.1 ? v.toFixed(2) : m.step < 1 ? v.toFixed(1) : v.toFixed(0));
    const cells = ecu.rpm.map((r, i) => {
      const d = cur[i] - st[i];
      const cls = Math.abs(d) < 1e-6 ? '' : d > 0 ? ' up' : ' down';
      return `<button class="cell${cls}${sel.has(i) ? ' sel' : ''}" data-cell="${i}"><small>${r}</small><b>${fmt(cur[i])}</b></button>`;
    }).join('');
    return `${noEcu ? '<p class="warn">Mașina asta nu are ECU (carburator și ruptor): se reglează doar jiclorul (λ) și avansul de la distribuitor.</p>' : ''}
      <div class="chips">${maps.filter((x) => !noEcu || x.key === 'lambda' || x.key === 'timingDeg').map((x) => `<button data-map="${x.key}" aria-pressed="${x.key === mapKey}">${esc(x.label)}</button>`).join('')}</div>
      <div class="cells">${cells}</div>
      <div class="row">
        <button data-op="all">Toate</button><button data-op="none">Niciuna</button>
        <button data-op="-">− ${m.step} ${m.unit}</button><button data-op="+">+ ${m.step} ${m.unit}</button>
        <button data-op="-5%">−5%</button><button data-op="+5%">+5%</button><button data-op="stock">Serie</button>
      </div>
      <div class="row"><label>Valoare <input type="number" step="${m.step}" data-set></label><button data-op="set">Pune pe selecție</button>
        <button data-op="undo" ${undo.length ? '' : 'disabled'}>↶ Undo</button><button data-op="redo" ${redo.length ? '' : 'disabled'}>↷ Redo</button></div>
      <canvas class="mapchart" width="640" height="180"></canvas>
      <div class="row">
        <label>Limitator <input type="number" step="100" value="${ecu.limiterRpm}" data-scalar="limiterRpm"> rpm</label>
        <label>Limitator viteză <input type="number" step="5" value="${ecu.speedLimitKmh}" data-scalar="speedLimitKmh"> km/h (0 = fără)</label>
        <label>Launch control <input type="number" step="100" value="${ecu.launchRpm}" data-scalar="launchRpm"> rpm (0 = oprit)</label>
      </div>
      <div class="row">${([0, 1, 2, 3] as Stage[]).map((s) => `<button data-stage="${s}">${s === 0 ? 'Totul de serie' : `Stage ${s}`}</button>`).join('')}</div>
      <div class="row"><button data-op="save">Salvează</button><button data-op="load">Încarcă salvarea</button><button data-op="export">Exportă fișier</button>
        <label class="file">Importă fișier<input type="file" accept="application/json,.json" data-import hidden></label><button data-op="forget">Șterge salvarea</button></div>`;
  }

  function partsTab(): string {
    const opt = (key: keyof Parts, label: string, vals: Array<[number | boolean, string]>) => `<div class="part"><span>${label}</span><span class="seg">${vals.map(([v, l]) => `<button data-part="${key}" data-v="${v}" aria-pressed="${parts[key] === v}">${l}</button>`).join('')}</span></div>`;
    return `${h.info.turbo ? opt('turboScale', 'Turbo', [[1, 'Serie'], [1.2, 'Hibrid'], [1.35, 'Mare'], [1.55, 'Foarte mare']]) : ''}
      ${h.info.turbo ? opt('intercooler', 'Intercooler', [[0.65, 'Serie'], [0.8, 'Sport'], [0.88, 'Front-mount']]) : ''}
      ${opt('exhaust', 'Evacuare', [[1, 'Serie'], [0.88, 'Sport'], [0.8, 'Downpipe și liberă']])}
      ${opt('intake', 'Admisie', [[1, 'Serie'], [1.02, 'Sport'], [1.04, 'Galerie și clapetă mari']])}
      ${opt('injectors', 'Injectoare', [[1, 'Serie'], [1.2, '+20%'], [1.45, '+45%'], [1.8, '+80%']])}
      ${opt('forged', 'Pistoane și biele', [[false, 'Turnate'], [true, 'Forjate']])}
      ${opt('clutch', 'Ambreiaj', [[1, 'Serie'], [1.3, 'Întărit'], [1.6, 'Ceramic']])}
      ${h.info.fuel === 'petrol' ? opt('octane', 'Benzină', [[95, '95'], [98, '98'], [100, '100']]) : ''}
      <p class="muted small">Piesele schimbă parametri fizici: presiunea maximă a compresorului și momentul în care se umflă turbo-ul, temperatura aerului după intercooler, contrapresiunea, debitul injectoarelor, rezistența la detonație, capacitatea ambreiajului.</p>`;
  }

  function dynoTab(): string {
    if (!dyno) return '<p class="muted">Se măsoară pe stand…</p>';
    const peak = (rows: DynoRow[]) => {
      const p = rows.reduce((a, r) => (r.kw > a.kw ? r : a), rows[0]);
      const t = rows.reduce((a, r) => (r.nm > a.nm ? r : a), rows[0]);
      return { kw: p.kw, hp: p.kw * 1.3596, at: p.rpm, nm: t.nm, nmAt: t.rpm };
    };
    const s = peak(dyno.stock), c = peak(dyno.cur);
    const eff = h.info.efficiency;
    return `<div class="dyno-head"><div><b>Serie</b> ${s.hp.toFixed(0)} CP (${s.kw.toFixed(0)} kW) la ${s.at} rpm · ${s.nm.toFixed(0)} N·m la ${s.nmAt} rpm</div>
      <div><b class="acc">Acum</b> ${c.hp.toFixed(0)} CP (${c.kw.toFixed(0)} kW) la ${c.at} rpm · ${c.nm.toFixed(0)} N·m la ${c.nmAt} rpm · <b>${c.hp - s.hp >= 0 ? '+' : ''}${(c.hp - s.hp).toFixed(0)} CP</b></div>
      <div class="muted small">La volant, condiții DIN 70020 (1013 mbar, 20 °C). La roată ≈ ×${eff.toFixed(2)}: ${(c.hp * eff).toFixed(0)} CP.</div></div>
      <canvas class="dynochart" width="760" height="300"></canvas>
      <div class="row"><button data-op="csv">Exportă CSV</button><button data-op="redyno">Măsoară din nou</button></div>`;
  }

  function obdTab(): string {
    const t = h.telemetry();
    if (!t) return '<p class="muted">Fără date.</p>';
    const codes = CODES.filter(([b]) => (t[16] | 0) & b);
    const live: Array<[string, string]> = [['Turație', `${t[1].toFixed(0)} rpm`], ['Presiune turbo', `${t[3].toFixed(2)} bar`], ['λ', t[8].toFixed(2)], ['Retragere avans', `${t[9].toFixed(1)}°`], ['EGT', `${t[7].toFixed(0)} °C`], ['Lichid de răcire', `${t[6].toFixed(0)} °C`], ['Duty injectoare', `${(t[12] * 100).toFixed(0)}%`], ['Cuplu', `${t[10].toFixed(0)} N·m`], ['Avarie pistoane', `${(t[13] * 100).toFixed(0)}%`], ['Avarie turbo', `${(t[14] * 100).toFixed(0)}%`], ['Radiator', `${(t[19] * 100).toFixed(0)}%`], ['Motor', t[21] ? 'smuls din suporți' : t[15] ? 'pornit' : 'oprit']];
    return `<h4>Coduri de eroare</h4>${codes.length ? `<ul class="codes">${codes.map(([, c, d]) => `<li><b>${c}</b> ${d}</li>`).join('')}</ul>` : '<p class="ok">Niciun cod.</p>'}
      <h4>Live data</h4><table class="live">${live.map(([a, b]) => `<tr><th>${a}</th><td>${b}</td></tr>`).join('')}</table>`;
  }

  function drawMap(): void {
    const cv = root.querySelector('.mapchart') as HTMLCanvasElement | null;
    if (!cv) return;
    const g = cv.getContext('2d')!;
    const W = cv.width, H = cv.height, pad = 28;
    g.clearRect(0, 0, W, H);
    const cur = ecu[mapKey], st = h.stock[mapKey];
    const all = [...cur, ...st];
    let lo = Math.min(...all), hi = Math.max(...all);
    if (hi - lo < 1e-6) { lo -= 1; hi += 1; }
    const x = (i: number) => pad + ((W - 2 * pad) * i) / (ecu.rpm.length - 1);
    const y = (v: number) => H - pad - ((H - 2 * pad) * (v - lo)) / (hi - lo);
    g.strokeStyle = 'rgba(255,255,255,.15)';
    g.strokeRect(pad, pad, W - 2 * pad, H - 2 * pad);
    const line = (vals: number[], color: string, w: number) => { g.strokeStyle = color; g.lineWidth = w; g.beginPath(); vals.forEach((v, i) => (i ? g.lineTo(x(i), y(v)) : g.moveTo(x(i), y(v)))); g.stroke(); };
    line(st, '#8a96a3', 2);
    line(cur, '#ffb000', 3);
    g.fillStyle = '#a9b4c0';
    g.font = '12px system-ui';
    g.fillText(`${hi.toFixed(2)}`, 2, pad + 4);
    g.fillText(`${lo.toFixed(2)}`, 2, H - pad);
    g.fillText('gri = serie, galben = acum', W - 170, 16);
  }

  function drawDyno(): void {
    const cv = root.querySelector('.dynochart') as HTMLCanvasElement | null;
    if (!cv || !dyno) return;
    const g = cv.getContext('2d')!;
    const W = cv.width, H = cv.height, pad = 36;
    g.clearRect(0, 0, W, H);
    const rows = [...dyno.stock, ...dyno.cur];
    const r0 = Math.min(...rows.map((r) => r.rpm)), r1 = Math.max(...rows.map((r) => r.rpm));
    const maxNm = Math.max(...rows.map((r) => r.nm)) * 1.1, maxKw = Math.max(...rows.map((r) => r.kw)) * 1.1;
    const x = (r: number) => pad + ((W - 2 * pad) * (r - r0)) / (r1 - r0);
    const yN = (v: number) => H - pad - ((H - 2 * pad) * v) / maxNm;
    const yK = (v: number) => H - pad - ((H - 2 * pad) * v) / maxKw;
    g.strokeStyle = 'rgba(255,255,255,.12)';
    for (let r = Math.ceil(r0 / 1000) * 1000; r <= r1; r += 1000) { g.beginPath(); g.moveTo(x(r), pad); g.lineTo(x(r), H - pad); g.stroke(); g.fillStyle = '#a9b4c0'; g.font = '11px system-ui'; g.fillText(`${r}`, x(r) - 12, H - pad + 14); }
    const line = (data: DynoRow[], f: (r: DynoRow) => number, yy: (v: number) => number, color: string, dash: number[]) => {
      g.strokeStyle = color; g.lineWidth = 2.5; g.setLineDash(dash); g.beginPath();
      data.forEach((r, i) => (i ? g.lineTo(x(r.rpm), yy(f(r))) : g.moveTo(x(r.rpm), yy(f(r))))); g.stroke(); g.setLineDash([]);
    };
    line(dyno.stock, (r) => r.nm, yN, '#5aa9ff', [6, 5]);
    line(dyno.stock, (r) => r.kw, yK, '#ff6b5a', [6, 5]);
    line(dyno.cur, (r) => r.nm, yN, '#5aa9ff', []);
    line(dyno.cur, (r) => r.kw, yK, '#ff6b5a', []);
    if (h.info.turbo) {
      const maxB = Math.max(0.1, ...rows.map((r) => r.boost));
      line(dyno.cur, (r) => r.boost, (v) => H - pad - ((H - 2 * pad) * 0.35 * v) / maxB, '#7be38a', []);
    }
    g.fillStyle = '#eef2f6';
    g.font = '12px system-ui';
    g.fillText(`N·m (albastru, max ${maxNm.toFixed(0)})   kW (roșu, max ${maxKw.toFixed(0)})${h.info.turbo ? '   bar (verde)' : ''}   punctat = serie`, pad, 16);
  }

  async function measure(): Promise<void> {
    const [a, b] = await Promise.all([h.dyno(h.stock, STOCK_PARTS), h.dyno(ecu, parts)]);
    dyno = { stock: dynoRows(a), cur: dynoRows(b) };
    if (tab === 'dyno') render();
  }

  function render(): void {
    root.innerHTML = `<header class="g-head"><h2>Tuning</h2><span class="muted">${esc(h.carName)}</span><button class="icon" data-close aria-label="Închide">✕</button></header>
      <div class="ecu-tabs seg">${(['maps', 'parts', 'dyno', 'obd'] as Tab[]).map((t) => `<button data-tab="${t}" aria-pressed="${t === tab}">${{ maps: 'Hărți ECU', parts: 'Piese', dyno: 'Dyno', obd: 'Diagnoză' }[t]}</button>`).join('')}</div>
      <div class="ecu-body">${tab === 'maps' ? mapTab() : tab === 'parts' ? partsTab() : tab === 'dyno' ? dynoTab() : obdTab()}${status ? `<p class="status">${esc(status)}</p>` : ''}</div>`;
    if (tab === 'maps') drawMap();
    if (tab === 'dyno') { if (!dyno) void measure(); else drawDyno(); }
  }

  root.onclick = async (e) => {
    const t = e.target as HTMLElement;
    const b = t.closest('button') as HTMLButtonElement | null;
    if (!b) return;
    if (b.dataset.close !== undefined) { window.clearInterval(obdTimer); root.hidden = true; root.innerHTML = ''; h.onClose(); return; }
    if (b.dataset.tab) { tab = b.dataset.tab as Tab; status = ''; render(); return; }
    if (b.dataset.map) { mapKey = b.dataset.map as MapKey; sel.clear(); render(); return; }
    if (b.dataset.cell) { const i = Number(b.dataset.cell); if (sel.has(i)) sel.delete(i); else sel.add(i); render(); return; }
    if (b.dataset.stage) { const r = applyStage(Number(b.dataset.stage) as Stage, h.stock, h.info); change(() => { ecu = r.ecu; parts = r.parts; }); status = `Aplicat ${b.textContent}`; render(); return; }
    if (b.dataset.part) {
      const key = b.dataset.part as keyof Parts, raw = b.dataset.v!;
      change(() => { (parts as unknown as Record<string, number | boolean>)[key] = raw === 'true' ? true : raw === 'false' ? false : Number(raw); });
      return;
    }
    const op = b.dataset.op;
    if (!op) return;
    const m = MAPS.find((x) => x.key === mapKey)!;
    const targets = sel.size ? [...sel] : [];
    const edit = (f: (v: number, i: number) => number) => {
      if (!targets.length) { status = 'Selectează întâi una sau mai multe celule (sau „Toate”).'; render(); return; }
      change(() => { for (const i of targets) ecu[mapKey][i] = Math.min(m.max, Math.max(m.min, +f(ecu[mapKey][i], i).toFixed(3))); });
    };
    if (op === 'all') { ecu.rpm.forEach((_, i) => sel.add(i)); render(); }
    else if (op === 'none') { sel.clear(); render(); }
    else if (op === '+') edit((v) => v + m.step);
    else if (op === '-') edit((v) => v - m.step);
    else if (op === '+5%') edit((v) => v * 1.05);
    else if (op === '-5%') edit((v) => v * 0.95);
    else if (op === 'stock') edit((_, i) => h.stock[mapKey][i]);
    else if (op === 'set') { const v = Number((root.querySelector('[data-set]') as HTMLInputElement).value); if (Number.isFinite(v)) edit(() => v); }
    else if (op === 'undo' && undo.length) { redo.push({ ecu: clone(ecu), parts: clone(parts) }); const s = undo.pop()!; ecu = s.ecu; parts = s.parts; h.apply(ecu, parts); dyno = null; render(); }
    else if (op === 'redo' && redo.length) { undo.push({ ecu: clone(ecu), parts: clone(parts) }); const s = redo.pop()!; ecu = s.ecu; parts = s.parts; h.apply(ecu, parts); dyno = null; render(); }
    else if (op === 'save') { await saveCalibration({ carId: h.carId, name: 'calibrare', ecu, parts, savedAt: new Date().toISOString() }); status = 'Salvat pe acest dispozitiv.'; render(); }
    else if (op === 'load') { const c = await loadCalibration(h.carId); if (c) change(() => { ecu = c.ecu; parts = c.parts; }); status = c ? `Încărcat (salvat ${new Date(c.savedAt).toLocaleString('ro-RO')}).` : 'Nu există o salvare pentru mașina asta.'; render(); }
    else if (op === 'forget') { await deleteCalibration(h.carId); status = 'Salvarea a fost ștearsă.'; render(); }
    else if (op === 'export') await exportCalibration({ carId: h.carId, name: h.carName, ecu, parts, savedAt: new Date().toISOString() });
    else if (op === 'csv' && dyno) {
      const lines = ['rpm,Nm_serie,kW_serie,Nm,kW,CP,bar,lambda,EGT_C,avans_grade', ...dyno.cur.map((r, i) => [r.rpm, dyno!.stock[i]?.nm.toFixed(1) ?? '', dyno!.stock[i]?.kw.toFixed(1) ?? '', r.nm.toFixed(1), r.kw.toFixed(1), (r.kw * 1.3596).toFixed(1), r.boost.toFixed(2), r.lambda.toFixed(3), r.egt.toFixed(0), r.timing.toFixed(1)].join(','))];
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/csv' }));
      a.download = `${h.carId}-dyno.csv`;
      a.click();
    } else if (op === 'redyno') { dyno = null; render(); }
  };
  root.onchange = async (e) => {
    const t = e.target as HTMLInputElement;
    if (t.dataset.scalar) { const k = t.dataset.scalar as 'limiterRpm' | 'speedLimitKmh' | 'launchRpm'; const v = Number(t.value); if (Number.isFinite(v)) change(() => { ecu[k] = v; }); }
    if (t.dataset.import !== undefined && t.files?.[0]) {
      try { const c = await importCalibration(await t.files[0].text(), h.carId); change(() => { ecu = c.ecu; parts = c.parts; }); status = 'Calibrare importată.'; }
      catch (err) { status = `Import refuzat: ${String((err as Error).message ?? err)}`; }
      render();
    }
  };
  obdTimer = window.setInterval(() => { if (tab === 'obd' && !root.hidden) render(); }, 700);
  root.hidden = false;
  render();
}
